import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { api } from '../api';
import SearchBox from './SearchBox';
import { Spinner, fmtPct, changeColor, curSym } from './UI';

const COLORS = ['#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#f472b6'];
const MARKET_SHORT = {
  MIL: 'Milano', NYSE: 'NYSE', NASDAQ: 'NASDAQ',
  XETRA: 'XETRA', PARIS: 'Parigi', LSE: 'Londra', RAW: 'Altro',
};
const PERIODS = [
  { label: '1M', value: '1mo' }, { label: '3M', value: '3mo' },
  { label: '6M', value: '6mo' }, { label: '1A', value: '1y' },
];

function useCompareData(stocks, period) {
  const [rows, setRows] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (stocks.length === 0) { setRows([]); setSeries([]); return; }
    setLoading(true);

    (async () => {
      // Quotazioni + tecnici per la tabella
      const rowResults = await Promise.all(stocks.map(async (s) => {
        try {
          const [q, tech] = await Promise.all([
            api.quote(s.ticker, s.market).catch(() => null),
            api.technical(s.ticker, s.market).catch(() => null),
          ]);
          return { ...s, quote: q, tech };
        } catch { return { ...s, quote: null, tech: null }; }
      }));

      // Storici per il grafico normalizzato
      const histResults = await Promise.all(stocks.map(async (s) => {
        try { return { ...s, hist: await api.history(s.ticker, period, s.market) }; }
        catch { return { ...s, hist: [] }; }
      }));

      if (cancelled) return;
      setRows(rowResults);

      // Costruisci serie normalizzata a base 100 per data
      const byDate = {};
      histResults.forEach((s, idx) => {
        const h = s.hist || [];
        if (!h.length) return;
        const base = h[0].close;
        h.forEach(pt => {
          if (!byDate[pt.date]) byDate[pt.date] = { date: pt.date };
          byDate[pt.date][`s${idx}`] = base ? +(pt.close / base * 100).toFixed(2) : null;
        });
      });
      const merged = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
      setSeries(merged);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [stocks, period]);

  return { rows, series, loading };
}

function Cell({ children, color }) {
  return <td style={{ padding: '10px 12px', fontSize: 13, color: color || '#cbd5e1', fontFamily: "'IBM Plex Mono', monospace", borderBottom: '1px solid #161c28', textAlign: 'right', whiteSpace: 'nowrap' }}>{children}</td>;
}
function RowLabel({ children }) {
  return <td style={{ padding: '10px 12px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #161c28', position: 'sticky', left: 0, background: '#0d1117' }}>{children}</td>;
}

export default function ComparePage({ initialStocks = [], onOpen }) {
  const [stocks, setStocks] = useState(initialStocks.slice(0, 6));
  const [period, setPeriod] = useState('6mo');

  useEffect(() => { setStocks(initialStocks.slice(0, 6)); }, [initialStocks]);

  const { rows, series, loading } = useCompareData(stocks, period);

  const addStock = (o) => {
    setStocks(prev => {
      if (prev.length >= 6) return prev;
      if (prev.some(s => s.ticker === o.ticker && s.market === o.market)) return prev;
      return [...prev, { ticker: o.ticker, market: o.market, name: o.name }];
    });
  };
  const removeStock = (i) => setStocks(prev => prev.filter((_, idx) => idx !== i));

  const metric = (r, fn, fmt = (v) => v, color) => {
    const v = r.quote || r.tech ? fn(r) : null;
    return v == null ? <Cell color="#334155">—</Cell> : <Cell color={color}>{fmt(v)}</Cell>;
  };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>Confronto titoli</h1>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
        Aggiungi fino a 6 titoli (anche di borse diverse). Il grafico mostra la performance
        in percentuale, con tutti i titoli riportati a base 100 alla data iniziale — così il
        confronto è valido anche tra valute diverse.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ width: 320 }}><SearchBox onSelect={addStock} /></div>
        <span style={{ fontSize: 12, color: '#475569' }}>{stocks.length}/6 titoli</span>
      </div>

      {stocks.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {stocks.map((s, i) => (
            <span key={`${s.market}:${s.ticker}`} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13,
              background: '#131720', border: `1px solid ${COLORS[i]}55`, borderRadius: 20, padding: '4px 12px',
            }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i] }} />
              <span style={{ color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>{s.ticker}</span>
              <button onClick={() => removeStock(i)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 14 }}>×</button>
            </span>
          ))}
        </div>
      )}

      {stocks.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#475569', fontSize: 14, background: '#0d1117', border: '1px solid #1e2736', borderRadius: 12 }}>
          Cerca un titolo qui sopra per iniziare il confronto.
        </div>
      ) : loading ? (
        <Spinner size={24} />
      ) : (
        <>
          <div style={{ background: '#131720', border: '1px solid #1e2736', borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Performance (base 100)</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {PERIODS.map(p => (
                  <button key={p.value} onClick={() => setPeriod(p.value)} style={{
                    fontSize: 12, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                    background: period === p.value ? '#1e3a5f' : 'transparent',
                    color: period === p.value ? '#60a5fa' : '#475569',
                    border: period === p.value ? '1px solid #2563eb' : '1px solid transparent',
                  }}>{p.label}</button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#475569', fontSize: 10 }} tickLine={false} axisLine={false}
                  minTickGap={40} tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fill: '#475569', fontSize: 10 }} tickLine={false} axisLine={false}
                  width={44} orientation="right" tickFormatter={v => v} />
                <Tooltip contentStyle={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#94a3b8' }} formatter={(v, n) => {
                    const idx = parseInt(n.replace('s', ''), 10);
                    return [`${v} (${v >= 100 ? '+' : ''}${(v - 100).toFixed(1)}%)`, stocks[idx]?.ticker || n];
                  }} />
                <Legend formatter={(val) => { const idx = parseInt(val.replace('s', ''), 10); return stocks[idx]?.ticker || val; }} />
                {stocks.map((s, i) => (
                  <Line key={i} dataKey={`s${i}`} stroke={COLORS[i]} strokeWidth={1.6} dot={false} connectNulls name={`s${i}`} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="compare-table-wrap" style={{ background: '#0d1117', border: '1px solid #1e2736', borderRadius: 12, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', position: 'sticky', left: 0, background: '#0d1117' }}></th>
                  {rows.map((r, i) => (
                    <th key={i} style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #1e2736' }}>
                      <button onClick={() => onOpen(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS[i] }} />
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', fontFamily: "'IBM Plex Mono', monospace" }}>{r.ticker}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{MARKET_SHORT[r.market] || r.market}</div>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr><RowLabel>Prezzo</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.quote?.price != null ? `${curSym(x.quote.currency)}${x.quote.price.toFixed(2)}` : null, v => v)}</React.Fragment>)}</tr>
                <tr><RowLabel>Var. giorno</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{r.quote?.change_pct != null ? <Cell color={changeColor(r.quote.change_pct)}>{fmtPct(r.quote.change_pct)}</Cell> : <Cell color="#334155">—</Cell>}</React.Fragment>)}</tr>
                <tr><RowLabel>P/E</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.quote?.pe_ratio, v => v.toFixed(1))}</React.Fragment>)}</tr>
                <tr><RowLabel>Div. Yield</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.quote?.dividend_yield, v => `${v}%`, '#10b981')}</React.Fragment>)}</tr>
                <tr><RowLabel>ROE</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.quote?.roe, v => `${v}%`)}</React.Fragment>)}</tr>
                <tr><RowLabel>Beta</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.quote?.beta, v => v.toFixed(2))}</React.Fragment>)}</tr>
                <tr><RowLabel>RSI (14)</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.tech?.indicators?.rsi_14, v => v.toFixed(0))}</React.Fragment>)}</tr>
                <tr><RowLabel>Trend (ADX)</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{r.tech?.signals?.trend ? <Cell color={r.tech.signals.trend === 'RIALZISTA' ? '#10b981' : r.tech.signals.trend === 'RIBASSISTA' ? '#f87171' : '#94a3b8'}>{r.tech.signals.trend}</Cell> : <Cell color="#334155">—</Cell>}</React.Fragment>)}</tr>
                <tr><RowLabel>Punteggio tecnico</RowLabel>{rows.map((r, i) => <React.Fragment key={i}>{metric(r, x => x.tech?.score_technical, v => `${v}/100`, '#60a5fa')}</React.Fragment>)}</tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
